// Copyright 2015, Mike Bostock
// BSD 3-Clause License — https://github.com/d3/d3-sankey/blob/main/LICENSE
// Vendored from d3-sankey@0.12.3 — https://github.com/d3/d3-sankey

import { max, min, sum } from 'd3-array';

import { justify } from './align.js';
import constant from './constant.js';
import type {
  SankeyExtraProperties,
  SankeyGraph,
  SankeyLayout,
} from './types.js';

// Internal working types — the algorithm mutates input objects in place to add geometry fields.

interface WorkingNode {
  index: number;
  depth: number;
  height: number;
  layer: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  value: number;
  fixedValue?: number;
  sourceLinks: WorkingLink[];
  targetLinks: WorkingLink[];
  [key: string]: unknown;
}

// Before computeNodeLinks resolves source/target ids, they may still be strings or numbers.
interface UnresolvedLink {
  index: number;
  source: WorkingNode | string | number;
  target: WorkingNode | string | number;
  value: number;
  y0: number;
  y1: number;
  width: number;
  [key: string]: unknown;
}

interface WorkingLink extends UnresolvedLink {
  source: WorkingNode;
  target: WorkingNode;
}

interface WorkingGraph {
  nodes: WorkingNode[];
  links: WorkingLink[];
}

function ascendingSourceBreadth(a: WorkingLink, b: WorkingLink): number {
  return a.source.y0 - b.source.y0 || a.index - b.index;
}

function ascendingTargetBreadth(a: WorkingLink, b: WorkingLink): number {
  return a.target.y0 - b.target.y0 || a.index - b.index;
}

function nodeValue(d: WorkingNode): number {
  return d.value;
}

function linkValue(d: WorkingLink): number {
  return d.value;
}

function findNode(
  nodeById: Map<string | number, WorkingNode>,
  id: string | number,
): WorkingNode {
  const node = nodeById.get(id);
  if (!node) {
    throw new Error('missing: ' + id);
  }
  return node;
}

function computeLinkBreadths({ nodes }: WorkingGraph): void {
  for (const node of nodes) {
    let y0 = node.y0;
    let y1 = y0;
    for (const link of node.sourceLinks) {
      link.y0 = y0 + link.width / 2;
      y0 += link.width;
    }
    for (const link of node.targetLinks) {
      link.y1 = y1 + link.width / 2;
      y1 += link.width;
    }
  }
}

export default function Sankey<
  N extends SankeyExtraProperties,
  L extends SankeyExtraProperties,
>(): SankeyLayout<SankeyGraph<N, L>, N, L> {
  let bx0 = 0,
    by0 = 0,
    bx1 = 1,
    by1 = 1;
  let dx = 24;
  let dy = 8,
    py = 8;
  let idAccessor: (d: WorkingNode) => string | number = (d) => d.index;
  let alignFn: (node: WorkingNode, n: number) => number = justify;
  let sortFn: ((a: WorkingNode, b: WorkingNode) => number) | undefined | null;
  let linkSortFn:
    | ((a: WorkingLink, b: WorkingLink) => number)
    | undefined
    | null;
  let nodesAccessor: (data: SankeyGraph<N, L>) => WorkingNode[] = (g) =>
    g.nodes as unknown as WorkingNode[];
  let linksAccessor: (data: SankeyGraph<N, L>) => WorkingLink[] = (g) =>
    g.links as unknown as WorkingLink[];
  let iterations = 6;

  function computeNodeLinks(graph: WorkingGraph): void {
    for (const [i, node] of graph.nodes.entries()) {
      node.index = i;
      node.sourceLinks = [];
      node.targetLinks = [];
    }
    const nodeById = new Map<string | number, WorkingNode>(
      graph.nodes.map((d) => [idAccessor(d), d]),
    );
    const unresolvedLinks = graph.links as unknown as UnresolvedLink[];
    for (const [i, link] of unresolvedLinks.entries()) {
      link.index = i;
      if (typeof link.source !== 'object') {
        link.source = findNode(nodeById, link.source);
      }
      if (typeof link.target !== 'object') {
        link.target = findNode(nodeById, link.target);
      }
      (link.source as WorkingNode).sourceLinks.push(link as WorkingLink);
      (link.target as WorkingNode).targetLinks.push(link as WorkingLink);
    }
    if (linkSortFn !== null && linkSortFn !== undefined) {
      for (const { sourceLinks, targetLinks } of graph.nodes) {
        sourceLinks.sort(linkSortFn);
        targetLinks.sort(linkSortFn);
      }
    }
  }

  function computeNodeValues({ nodes }: WorkingGraph): void {
    for (const node of nodes) {
      node.value =
        node.fixedValue === undefined
          ? Math.max(
              sum(node.sourceLinks, linkValue),
              sum(node.targetLinks, linkValue),
            )
          : node.fixedValue;
    }
  }

  function computeNodeDepths({ nodes }: WorkingGraph): void {
    const n = nodes.length;
    let current = new Set(nodes);
    let next = new Set<WorkingNode>();
    let x = 0;
    let iterations = 0;
    while (current.size) {
      let maxPinnedDepth = -1;
      for (const node of current) {
        node.depth = typeof node['column'] === 'number' ? node['column'] : x;
        if (
          typeof node['column'] === 'number' &&
          node['column'] > maxPinnedDepth
        ) {
          maxPinnedDepth = node['column'];
        }
        for (const { target } of node.sourceLinks) {
          next.add(target);
        }
      }
      if (++iterations > n) {
        throw new Error('circular link');
      }
      x = Math.max(x + 1, maxPinnedDepth + 1);
      current = next;
      next = new Set();
    }
  }

  function computeNodeHeights({ nodes }: WorkingGraph): void {
    const n = nodes.length;
    let current = new Set(nodes);
    let next = new Set<WorkingNode>();
    let x = 0;
    while (current.size) {
      for (const node of current) {
        node.height = x;
        for (const { source } of node.targetLinks) {
          next.add(source);
        }
      }
      if (++x > n) {
        throw new Error('circular link');
      }
      current = next;
      next = new Set();
    }
  }

  function computeNodeLayers({ nodes }: WorkingGraph): WorkingNode[][] {
    const x = (max(nodes, (d) => d.depth) ?? 0) + 1;
    const kx = (bx1 - bx0 - dx) / (x - 1);
    const columns = new Array<WorkingNode[]>(x);
    for (const node of nodes) {
      const i = Math.max(0, Math.min(x - 1, Math.floor(alignFn(node, x))));
      node.layer = i;
      node.x0 = bx0 + i * kx;
      node.x1 = node.x0 + dx;
      if (columns[i]) {
        columns[i].push(node);
      } else {
        columns[i] = [node];
      }
    }
    // alignFn may remap nodes so that some indices in the sparse array remain empty.
    // Filter before downstream passes to prevent crashes on undefined column entries.
    const compact = columns.filter((c): c is WorkingNode[] => c !== undefined);
    if (sortFn) {
      for (const column of compact) {
        column.sort(sortFn);
      }
    }
    return compact;
  }

  function initializeNodeBreadths(columns: WorkingNode[][]): void {
    const ky =
      min(
        columns,
        (c) => (by1 - by0 - (c.length - 1) * py) / sum(c, nodeValue),
      ) ?? 0;
    for (const nodes of columns) {
      let y = by0;
      for (const node of nodes) {
        node.y0 = y;
        node.y1 = y + node.value * ky;
        y = node.y1 + py;
        for (const link of node.sourceLinks) {
          link.width = link.value * ky;
        }
      }
      y = (by1 - y + py) / (nodes.length + 1);
      for (let i = 0; i < nodes.length; ++i) {
        const node = nodes[i];
        node.y0 += y * (i + 1);
        node.y1 += y * (i + 1);
      }
      reorderLinks(nodes);
    }
  }

  function computeNodeBreadths(graph: WorkingGraph): void {
    const columns = computeNodeLayers(graph);
    py = Math.min(dy, (by1 - by0) / ((max(columns, (c) => c.length) ?? 1) - 1));
    initializeNodeBreadths(columns);
    for (let i = 0; i < iterations; ++i) {
      const alpha = Math.pow(0.99, i);
      const beta = Math.max(1 - alpha, (i + 1) / iterations);
      relaxRightToLeft(columns, alpha, beta);
      relaxLeftToRight(columns, alpha, beta);
    }
  }

  function relaxLeftToRight(
    columns: WorkingNode[][],
    alpha: number,
    beta: number,
  ): void {
    for (let i = 1, n = columns.length; i < n; ++i) {
      const column = columns[i];
      for (const target of column) {
        let y = 0;
        let w = 0;
        for (const { source, value } of target.targetLinks) {
          const v = value * (target.layer - source.layer);
          y += targetTop(source, target) * v;
          w += v;
        }
        if (!(w > 0)) {
          continue;
        }
        const dy = (y / w - target.y0) * alpha;
        target.y0 += dy;
        target.y1 += dy;
        reorderNodeLinks(target);
      }
      if (sortFn === undefined) {
        column.sort(ascendingBreadth);
      }
      resolveCollisions(column, beta);
    }
  }

  function relaxRightToLeft(
    columns: WorkingNode[][],
    alpha: number,
    beta: number,
  ): void {
    for (let n = columns.length, i = n - 2; i >= 0; --i) {
      const column = columns[i];
      for (const source of column) {
        let y = 0;
        let w = 0;
        for (const { target, value } of source.sourceLinks) {
          const v = value * (target.layer - source.layer);
          y += sourceTop(source, target) * v;
          w += v;
        }
        if (!(w > 0)) {
          continue;
        }
        const dy = (y / w - source.y0) * alpha;
        source.y0 += dy;
        source.y1 += dy;
        reorderNodeLinks(source);
      }
      if (sortFn === undefined) {
        column.sort(ascendingBreadth);
      }
      resolveCollisions(column, beta);
    }
  }

  function ascendingBreadth(a: WorkingNode, b: WorkingNode): number {
    return a.y0 - b.y0;
  }

  function resolveCollisions(nodes: WorkingNode[], alpha: number): void {
    const i = nodes.length >> 1;
    const subject = nodes[i];
    resolveCollisionsBottomToTop(nodes, subject.y0 - py, i - 1, alpha);
    resolveCollisionsTopToBottom(nodes, subject.y1 + py, i + 1, alpha);
    resolveCollisionsBottomToTop(nodes, by1, nodes.length - 1, alpha);
    resolveCollisionsTopToBottom(nodes, by0, 0, alpha);
  }

  function resolveCollisionsTopToBottom(
    nodes: WorkingNode[],
    y: number,
    i: number,
    alpha: number,
  ): void {
    for (; i < nodes.length; ++i) {
      const node = nodes[i];
      const dy = (y - node.y0) * alpha;
      if (dy > 1e-6) {
        node.y0 += dy;
        node.y1 += dy;
      }
      y = node.y1 + py;
    }
  }

  function resolveCollisionsBottomToTop(
    nodes: WorkingNode[],
    y: number,
    i: number,
    alpha: number,
  ): void {
    for (; i >= 0; --i) {
      const node = nodes[i];
      const dy = (node.y1 - y) * alpha;
      if (dy > 1e-6) {
        node.y0 -= dy;
        node.y1 -= dy;
      }
      y = node.y0 - py;
    }
  }

  function reorderNodeLinks({ sourceLinks, targetLinks }: WorkingNode): void {
    if (linkSortFn === undefined) {
      for (const {
        source: { sourceLinks: sl },
      } of targetLinks) {
        sl.sort(ascendingTargetBreadth);
      }
      for (const {
        target: { targetLinks: tl },
      } of sourceLinks) {
        tl.sort(ascendingSourceBreadth);
      }
    }
  }

  function reorderLinks(nodes: WorkingNode[]): void {
    if (linkSortFn === undefined) {
      for (const { sourceLinks, targetLinks } of nodes) {
        sourceLinks.sort(ascendingTargetBreadth);
        targetLinks.sort(ascendingSourceBreadth);
      }
    }
  }

  function targetTop(source: WorkingNode, target: WorkingNode): number {
    let y = source.y0 - ((source.sourceLinks.length - 1) * py) / 2;
    for (const { target: node, width } of source.sourceLinks) {
      if (node === target) {
        break;
      }
      y += width + py;
    }
    for (const { source: node, width } of target.targetLinks) {
      if (node === source) {
        break;
      }
      y -= width;
    }
    return y;
  }

  function sourceTop(source: WorkingNode, target: WorkingNode): number {
    let y = target.y0 - ((target.targetLinks.length - 1) * py) / 2;
    for (const { source: node, width } of target.targetLinks) {
      if (node === source) {
        break;
      }
      y += width + py;
    }
    for (const { target: node, width } of source.sourceLinks) {
      if (node === target) {
        break;
      }
      y -= width;
    }
    return y;
  }

  function runLayout(data: SankeyGraph<N, L>): SankeyGraph<N, L> {
    const graph: WorkingGraph = {
      nodes: nodesAccessor(data),
      links: linksAccessor(data),
    };
    computeNodeLinks(graph);
    computeNodeValues(graph);
    computeNodeDepths(graph);
    computeNodeHeights(graph);
    computeNodeBreadths(graph);
    computeLinkBreadths(graph);
    return graph as unknown as SankeyGraph<N, L>;
  }

  // Build the fluent SankeyLayout object using Object.assign on the callable.
  // The type assertion at the return bridges the simplified optional-parameter
  // method signatures to the overloaded SankeyLayout interface.
  const layout = Object.assign(runLayout, {
    update(graph: SankeyGraph<N, L>): SankeyGraph<N, L> {
      computeLinkBreadths(graph as unknown as WorkingGraph);
      return graph;
    },

    nodeId(fn?: (node: WorkingNode) => string | number) {
      if (fn === undefined) {
        return idAccessor;
      }
      idAccessor = fn;
      return layout;
    },

    nodeAlign(fn?: (node: WorkingNode, n: number) => number) {
      if (fn === undefined) {
        return alignFn;
      }
      alignFn = typeof fn === 'function' ? fn : constant(fn)();
      return layout;
    },

    nodeSort(fn?: ((a: WorkingNode, b: WorkingNode) => number) | null) {
      if (arguments.length === 0) {
        return sortFn;
      }
      sortFn = fn;
      return layout;
    },

    nodeWidth(w?: number) {
      if (w === undefined) {
        return dx;
      }
      dx = +w;
      return layout;
    },

    nodePadding(p?: number) {
      if (p === undefined) {
        return dy;
      }
      dy = py = +p;
      return layout;
    },

    nodes(
      accessor?:
        | Array<SankeyGraph<N, L>['nodes'][number]>
        | ((data: SankeyGraph<N, L>) => WorkingNode[]),
    ) {
      if (accessor === undefined) {
        return nodesAccessor;
      }
      nodesAccessor =
        typeof accessor === 'function'
          ? accessor
          : constant(accessor as unknown as WorkingNode[]);
      return layout;
    },

    links(
      accessor?:
        | Array<SankeyGraph<N, L>['links'][number]>
        | ((data: SankeyGraph<N, L>) => WorkingLink[]),
    ) {
      if (accessor === undefined) {
        return linksAccessor;
      }
      linksAccessor =
        typeof accessor === 'function'
          ? accessor
          : constant(accessor as unknown as WorkingLink[]);
      return layout;
    },

    linkSort(fn?: ((a: WorkingLink, b: WorkingLink) => number) | null) {
      if (arguments.length === 0) {
        return linkSortFn;
      }
      linkSortFn = fn;
      return layout;
    },

    size(s?: [number, number]) {
      if (s === undefined) {
        return [bx1 - bx0, by1 - by0] as [number, number];
      }
      bx0 = by0 = 0;
      bx1 = +s[0];
      by1 = +s[1];
      return layout;
    },

    extent(e?: [[number, number], [number, number]]) {
      if (e === undefined) {
        return [
          [bx0, by0],
          [bx1, by1],
        ] as [[number, number], [number, number]];
      }
      bx0 = +e[0][0];
      bx1 = +e[1][0];
      by0 = +e[0][1];
      by1 = +e[1][1];
      return layout;
    },

    iterations(n?: number) {
      if (n === undefined) {
        return iterations;
      }
      iterations = +n;
      return layout;
    },
  });

  return layout as unknown as SankeyLayout<SankeyGraph<N, L>, N, L>;
}
